import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useAccount, useDisconnect, useSwitchChain } from 'wagmi';
import { atom, useAtom } from 'jotai';
import { useConnectModal } from '@rainbow-me/rainbowkit';

import { ABS_CHAIN, IS_TESTNET } from '~/core/constants';
import { useToast } from '~/core/contexts/ToastContext';
import { useSignMessage } from '~/core/hooks/useSignMessage';
import { useConnect } from '~/core/hooks/useConnect';
import { api, isBlinkoLoginTokenValid } from '~/blinko/api';
import { InnerShape } from '../components/Shape';
import { useSound } from '../components/SoundHandler';
import { DotDotDot } from '~/core/components/DotDotDot';
import { atomAllowAllWallets, atomIsIgamingDemo } from '../states';
import { useAtomValue } from 'jotai';

export const atomWalletModal = {
  visible: atom<boolean>(false),
  isSigned: atom<null | boolean>(null),
};

export const WalletModal = () => {
  const { address, isConnected, isReconnecting } = useAccount();
  const { signMessageAsync } = useSignMessage();
  const { connectAsync } = useConnect();
  const { switchChainAsync } = useSwitchChain();
  const { openConnectModal } = useConnectModal();
  const { connectors } = useDisconnect();
  const toast = useToast();
  /* const connections = useConnections(); */
  const sound = useSound();
  
  const [isSigned, setIsSigned] = useAtom(atomWalletModal.isSigned);
  const [visible, setVisible] = useAtom(atomWalletModal.visible);
  const [allowAllWallets] = useAtom(atomAllowAllWallets)
  const isIgamingDemo = useAtomValue(atomIsIgamingDemo)

  const [isConnecting, setIsConnecting] = useState(false);
  const [isSigning, setIsSigning] = useState(false);

  useEffect(() => {
    if (!isReconnecting) {
      if (isConnected && address) {
        setIsSigned(isBlinkoLoginTokenValid(address));
      } else {
        setIsSigned(false);
      }
    }
  }, [isConnected, isReconnecting, address]);

  useEffect(() => {
    if (isConnected && isSigned !== null) {
      setVisible(!isSigned);
    }
  }, [isConnected && isSigned]);

  useEffect(() => {
    let intervalId: number;
    const checkTokenExpiration = () => {
      if (isConnected && address && !isBlinkoLoginTokenValid(address))
        setIsSigned(false);
    };
    if (isConnected && address)
      intervalId = window.setInterval(checkTokenExpiration, 1000);
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isConnected, address]);

  const onSignIn = async () => {
    if (isSigning) return;
    if (!isConnected || !address) return;

    setIsSigning(true);
    try {
      const signature = await signMessageAsync({ message: "Sign in to Blinko" });
      try {
        await api.login(address, signature);
        setIsSigned(true);
      } catch (error: any) {
        toast.error(error?.message);
      }
    } catch (error) {
      toast.error('Sign in cancelled');
    } finally {
      setIsSigning(false);
    }
  };

  const onConnect = async () => {
    if (isSigning || isConnecting) return;

    setIsConnecting(true);
    
    try {
      await switchChainAsync({ 
        chainId: ABS_CHAIN.id, 
        addEthereumChainParameter: {
          chainName: ABS_CHAIN.name,
          nativeCurrency: ABS_CHAIN.nativeCurrency,
          rpcUrls: ABS_CHAIN.rpcUrls.default.http,
          blockExplorerUrls: [ABS_CHAIN.blockExplorers.native.url],
        },
      });
      await connectAsync();
    } catch (error) {
      toast.error('Connect cancelled');
      console.error(error);
      setVisible(false);
    } finally {
      setIsConnecting(false);
    }
  };

  const onClick = () => {
    sound.play('click');
    if (isSigning || isConnecting) 
      return;

    if (isConnected) {
      onSignIn();
    } else {
      onConnect();
    }
  };

  const onOutsideClick = () => {
    if (isConnected || isConnecting) 
      return;

    setVisible(false);
  };

  const onCancel = () => {
    sound.play('click');
    if (isConnecting || isSigning) 
      return;

    if (isConnected) {
      connectors.forEach(connector => {
        connector.disconnect();
      });
      localStorage.clear();
      setVisible(false);
      return;
    }

    onOutsideClick();
  };

  useEffect(() => {
    if (isIgamingDemo) return
    if (visible && !isConnected) {
      if (allowAllWallets) {
        openConnectModal?.()
      } else if (IS_TESTNET) {
        openConnectModal?.()
      } else {
        onConnect()
      }
    }
  }, [visible, isConnected, isIgamingDemo])

  if (isIgamingDemo) {
    return null
  }

  if (!visible) {
    return null
  }

  if (isSigned) {
    return null
  }

  if (isConnecting || isReconnecting) {
    return null
  }

  return (
      <div
        className='fixed z-40 inset-0 flex items-center justify-center'
      >
        <motion.div
          className='absolute inset-0 bg-black/60'
        />

        <div className='relative flex flex-col items-center justify-center gap-2'>
          <button onClick={onClick}>
            <InnerShape cornerDiagonal={14} cornerRadius={8} strokeWidth={1}>
              <div className="flex justify-center items-center gap-3 px-8 py-3">
                {isSigning ? (
                  <div className='text-base font-blinko text-white'>
                    <DotDotDot>
                      {addDots => (
                        <span className='relative'>Signing<span>{addDots()}</span></span>
                      )}
                    </DotDotDot>
                  </div>
                ) : (
                  <p className="text-base font-blinko text-white">Sign in to Blinko</p>
                )}
              </div>
            </InnerShape>
          </button>

          <button onClick={onCancel}>
            <div className="flex justify-center items-center gap-3 px-8 py-3">
              <p className="text-sm font-blinko text-white">Disconnect</p>
            </div>
          </button>
        </div>
    </div>
  );
};
